/**
 * Javascript file for the current canvas brick page for the current business model.
 * @author Adrian Rebmann, Michael Frey, Andrea Villeda
 */

import {
    getQuestionsForBrick,
    getBusinessModelSubgraph,
    postBusinessModelSubgraph,
    updateBrick,
    getBusinessModelByHash,
} from "../../../../api";
export default {
    name: "Brick",
    props: ["name", "bmname", "hash", "brickhash"],

    /**
     * TODO: finish filling these field.
     * The vue instance's data object.
     * @property {string} currenttext - The description text for the current item being hovered over.
     * @property {string} currenthelp - The name of the current item being hovered over.
     * @property {boolean} customAdd - Option to create a custom aspect's visibility.
     * @property {string} currentaspect.description - The description of the current aspect.
     * @property {string} currentaspect.name - The name of the current aspect.
     * @property {string} aspecttoadd.description - The description of the aspect to add.
     * @property {string} aspecttoadd.name - The name of the aspect to add.
     * @property {boolean} aspecttoadd.brick - The brick element of the aspect to add.
     * @property {object} businessmodel - The current business model.
     * @property {boolean} loading - Information is currently being loaded.
     * @property {boolean} saving - Information is currently being saved.
     * @property {Map} map - The map element for storing the questions.
     * @property {object} questionsequences - The questions for each sequence.
     * @property {number} seqidx - The index of the sequence.
     * @property {string} icon - The icon showing the current location in the canvas.
     * @property {object} brick - The current brick instance.
     * @property {string} currentquestion.text - The text of the current question.
     * @property {object} currentquestion.answers - The possible answers for the current question.
     * @property {string} currentquestion.type - The type of the current question.
     * @property {object} currentquestion.chosen - The chosen answers for the current question.
     * @property {string} currentquestion.bma - The business model aspects for the current question.
     * @property {object} currentquestion.other - Other information about the current question (e.g the index).
     */
    data() {
        return {
            currenttext: "",
            currenthelp: "",
            customAdd: false,
            currentaspect: {
                description: "",
                name: "",
            },
            aspecttoadd: {
                description: "",
                name: "",
                brick: false,
            },
            businessmodel: null,
            loading: false,
            saving: false,
            map: new Map(),
            questionsequences: null,
            seqidx: 0,
            icon: "",
            brick: null,
            currentquestion: {
                text: "",
                answers: [],
                type: "",
                chosen: [],
                bma: "",
                other: [],
            },
        };
    },

    /**
     * Called after the instance has been mounted.
     */
    mounted() {
        this.fetchData();
        this.currentquestion.other.push({
            idx: this.currentquestion.other.length,
            text: "",
        });
    },

    /**
     * The expressions to watch.
     */
    watch: {
        // call again the method if the route changes
        $route: "fetchData",
    },

    /**
     * The vue instance's method declaration.
     */
    methods: {
        /**
         * Deletes the elementinstance of the element to be removed.
         * @param {*} element - The element to be removed.
         */
        removeAspect(element) {
            let toRemove = -1;
            //iterate through all element instances until the instance of the element
            //to be removed is found.
            for (let i = 0; i < this.brick.elementinstances.length; i++) {
                if (this.brick.elementinstances[i].name == element.name) {
                    if (
                        element.hash &&
                        element.hash != this.brick.elementinstances[i].hash
                    ) {
                        continue;
                    } else {
                        toRemove = i;
                    }
                }
            }
            if (toRemove >= 0) {
                this.brick.elementinstances.splice(toRemove, 1);
            }
        },

        /**
         * Removes the child element of the element.
         * @param {*} element - The element whose child is to be removed.
         * @param {*} child - The child element to be removed.
         */
        removeChildAspect(element, child) {
            let toRemove = -1;
            for (let i = 0; i < this.brick.elementinstances.length; i++) {
                if (this.brick.elementinstances[i].name == element.name) {
                    if (
                        element.hash &&
                        element.hash != this.brick.elementinstances[i].hash
                    ) {
                        continue;
                    } else {
                        for (
                            let k = 0;
                            k < this.brick.elementinstances[i].children.length;
                            k++
                        ) {
                            if (
                                this.brick.elementinstances[i].children[k].name == child.name
                            ) {
                                if (
                                    child.hash &&
                                    child.hash != this.brick.elementinstances[i].children[k].hash
                                ) {
                                    continue;
                                } else {
                                    toRemove = k;
                                }
                            }
                        }
                        if (toRemove >= 0) {
                            this.brick.elementinstances[i].children.splice(toRemove, 1);
                            return;
                        }
                    }
                }
            }
            if (toRemove >= 0) {
                this.brick.elementinstances.splice(toRemove, 1);
            }
        },

        /**
         * Adds the brick to the element instances.
         */
        addToElementInstances() {
            if (this.aspecttoadd.brick) {
                this.currentaspect.elementinstances.unshift({
                    children: [],
                    description: this.aspecttoadd.description,
                    element: null,
                    hash: "",
                    name: this.aspecttoadd.name,
                });
            } else {
                this.currentaspect.children.unshift({
                    children: [],
                    description: this.aspecttoadd.description,
                    element: null,
                    hash: "",
                    name: this.aspecttoadd.name,
                });
            }
            this.aspecttoadd.name = "";
            this.aspecttoadd.description = "";
        },

        /**
         * Verifies if the user is done answering the questions for the brick with the same name as the 
         * name provided in the parameter. 
         * @param {string} name - The name of the brick to verify.
         * @returns - Boolean value of whether the  the user is done answering the questions for the brick with the 
         * same name as the name provided in the parameter.
         */
        isDone(name) {
            for (let i = 0; i < this.businessmodel.bricks.length; i++) {
                if (
                    this.businessmodel.bricks[i].name == name &&
                    this.businessmodel.bricks[i].donewithquestions == true
                )
                    return true;
            }
            return false;
        },

        /**
         * Goes one by one brick.
         */
        setBack() {
            this.seqidx--;
            if (this.questionsequences[this.seqidx]) {
                this.currentquestion.text = this.questionsequences[
                    this.seqidx
                ].questions[0].text;
                this.currentquestion.type = this.questionsequences[
                    this.seqidx
                ].questions[0].type;
                this.currentquestion.answers = this.questionsequences[
                    this.seqidx
                ].questions[0].answers;
                this.currentquestion.chosen = [];
                this.currentquestion.seqidx = this.seqidx;
            }
        },

        /**
         * Adds a new child element to the element provided in the paramenter.
         * @param {*} element - Element to add a child to.
         */
        addElement(element) {
            //console.log(child);
            element.children.push({
                children: [],
                description: element.children.length,
                element: null,
                hash: null,
                name: "",
            });
        },

        /**
         * Removes a child element from the element provided whose description matches the description in the parameter.
         * @param {*} element - The element to remove a child from.
         * @param {*} description - The description of the child element to be removed.
         */
        removeElement(element, description) {
            let toRemove = -1;
            for (let i = 0; i < element.children.length; i++) {
                if (element.children[i].description == description) {
                    toRemove = i;
                }
            }
            if (toRemove >= 0) {
                element.children.splice(toRemove, 1);
            }
        },

        /**
         * Resets all objects.
         */
        clearObjects() {
            //return new Promise(function(resolve, reject) {
            this.map = new Map();
            this.currenttext = "";
            this.currenthelp = "";
            this.questionsequences = null;
            this.seqidx = 0;
            this.icon = "";
            this.brick = null;
            this.businessmodel = null;
            this.currentquestion = {
                text: "",
                answers: [],
                type: "",
                chosen: [],
                bma: "",
                other: [],
            };
            //resolve("Stuff worked!");
            //});
        },

        /**
         * Verifies if the chosen answers include an element of type "other".
         * @returns {boolean} - Boolean value of whether the chosen answers include an element of type "other".
         */
        chosenIncludesOther() {
            for (let i = 0; i < this.currentquestion.chosen.length; i++) {
                if (JSON.parse(this.currentquestion.chosen[i]).bma == "Sonstiges") {
                    return true;
                }
            }
            return false;
        },

        /**
         * Verifies if the chosen answers include an element of type "no answer".
         * @param {*} selection - The selected answers.
         * @returns - Boolean value of whether the chosen answers include an element of type "no answer".
         */
        chosenIncludesNoAns(selection) {
            for (let i = 0; i < selection.length; i++) {
                if (JSON.parse(selection[i]).bma == "keine Antwort") {
                    return true;
                }
            }
            return false;
        },

        /**
         * Verifies if the current question's "other" section is empty.
         * @returns - Emptiness value of the current question's "other" section.
         */
        otherNotEmpty() {
            for (let i = 0; i < this.currentquestion.other.length; i++) {
                if (this.currentquestion.other[i].text == "") {
                    return false;
                }
            }
            return true;
        },

        /**
         * Adds the properties idx (index) and text to the current question's "other" section.
         */
        addToOther() {
            this.currentquestion.other.push({
                idx: this.currentquestion.other.length,
                text: "",
            });
        },

        /**
         * Removes an element from the "other" list.
         * @param {*} idx - The index of the element to be removed.
         */
        removeFromOther(idx) {
            let toRemove = -1;
            for (let i = 0; i < this.currentquestion.other.length; i++) {
                if (this.currentquestion.other[i].idx == idx) {
                    toRemove = i;
                }
            }
            if (toRemove >= 0) {
                this.currentquestion.other.splice(toRemove, 1);
            }
        },

        /**
         * Fetches the business model's data stored in the backend.
         */
        fetchData() {
            this.currenttext = "";
            this.currenthelp = "";
            //this.clearObjects();
            this.loading = true;
            this.icon = "/static/canvas_" + this.name + ".svg";
            this.icon = this.icon.replace("ü", "u").replace("ä", "a");
            getBusinessModelSubgraph(this.hash, this.name).then((res) => {
                //console.log(res);
                this.brick = res;
                getBusinessModelByHash(this.hash).then((res) => {
                    this.businessmodel = res;
                    this.loading = false;
                });
            });

            //get the questions
            getQuestionsForBrick(this.name).then((fromBackend) => {
              console.log(fromBackend);
                this.questionsequences = fromBackend;
                for (let i = 0; i < this.questionsequences.length; i++) {
                    for (let j = 0; j < this.questionsequences[i].questions.length; j++) {
                        if (
                            this.questionsequences[i].questions[j].referencedBy &&
                            this.questionsequences[i].questions[j].referencedBy !== null &&
                            this.questionsequences[i].questions[j].referencedBy !== "null" &&
                            this.questionsequences[i].questions[j].referencedBy !== undefined
                        ) {
                            this.map.set(
                                this.questionsequences[i].questions[j].referencedBy.hash,
                                this.questionsequences[i].questions[j]
                            );
                        }
                    }
                }
                this.currentquestion.text = this.questionsequences[
                    this.seqidx
                ].questions[this.seqidx].text;
                this.currentquestion.type = this.questionsequences[
                    this.seqidx
                ].questions[this.seqidx].type;
                this.currentquestion.answers = this.questionsequences[
                    this.seqidx
                ].questions[this.seqidx].answers;
                this.currentquestion.chosen = [];
                this.currentquestion.seqidx = this.seqidx;
            });
        },

        /**
         * Returns to the previous page.
         */
        goBack() {
            this.$router.push({
                name: "BusinessModel",
                params: { hash: this.hash, bmname: this.bmname },
            });
        },

        /**
         * Stringifies the parameter string (answer).
         * @returns - The result string.
         */
        stringifyAns(answer) {
            return JSON.stringify(answer);
        },

        /**
         * Marks the current question as done editing and saves the answers.
         */
        setDone() {
            if (this.currentquestion.other && this.currentquestion.other.length > 0) {
                let toRemove = -1;
                let toAdd = [];
                for (let i = 0; i < this.currentquestion.chosen.length; i++) {
                    if (JSON.parse(this.currentquestion.chosen[i]).bma == "Sonstiges") {
                        let parsed = JSON.parse(this.currentquestion.chosen[i]);
                        toRemove = i;
                        for (let j = 0; j < this.currentquestion.other.length; j++) {
                            let newBma = parsed;
                            if (this.currentquestion.other[j].text !== "") {
                                newBma.bma = this.currentquestion.other[j].text;
                                toAdd.push(JSON.stringify(newBma));
                            }
                        }
                        this.currentquestion.other = [];
                        this.currentquestion.other.push({
                            idx: this.currentquestion.other.length,
                            text: "",
                        });
                    }
                }
                if (toRemove >= 0) {
                    this.currentquestion.chosen.splice(toRemove, 1);
                }
                for (let k = 0; k < toAdd.length; k++)
                    this.currentquestion.chosen.push(toAdd[k]);
            }

            //Save the answers
            this.saveAnswer().then((res) => {
                this.seqidx++;
                if (this.questionsequences[this.seqidx]) {
                    this.currentquestion.text = this.questionsequences[
                        this.seqidx
                    ].questions[0].text;
                    this.currentquestion.type = this.questionsequences[
                        this.seqidx
                    ].questions[0].type;
                    this.currentquestion.answers = this.questionsequences[
                        this.seqidx
                    ].questions[0].answers;
                    this.currentquestion.chosen = [];
                    this.currentquestion.seqidx = this.seqidx;
                } else {
                    this.brick.donewithquestions = true;
                    this.updateBrick();
                }
            });
        },

        /**
         * Updates the brick and goes to the next question in the walkthrough.
         * @returns - Promise for the completion of the callback.
         */
        updateBrick() {
            this.loading = true;
            this.saving = true;
            return updateBrick(this.hash, this.name, this.brick).then((res) => {
                this.saving = false;
                this.loading = false;
                this.getNextInWalkthrough(this.name);
            });
        },

        /**
         * Updates the brick after having answered all questions.
         * @returns - Promise for the completion of the callback.
         */
        updateBrickToOverview() {
            this.saving = true;
            this.brick.donewithquestions = true;
            return updateBrick(this.hash, this.name, this.brick).then((res) => {
                this.saving = false;
            });
        },

        /**
         * Updates the brick without having answered all questions.
         * @returns - Promise for the completion of the callback.
         */
        updateBrickSetNotDone() {
            this.saving = true;
            this.brick.donewithquestions = false;
            return updateBrick(this.hash, this.name, this.brick).then((res) => {
                this.saving = false;
            });
        },

        /**
         * Saves the answer(s) of the current question.
         * @returns - Promise for the completion of the callback.
         */
        saveAnswer() {
            this.saving = true;
            let body = [];
            for (let i = 0; i < this.currentquestion.chosen.length; i++) {
                body.push(JSON.parse(this.currentquestion.chosen[i]));
            }
            return postBusinessModelSubgraph(this.hash, this.name, body).then(
                (res) => {
                    this.brick = res;
                    this.saving = false;
                }
            );
        },

        /**
         * Verifies if the boundaries were held in the answers.
         * If that was not the case, it excludes that answer.
         */
        checkBounds(selection, type) {
            if (this.chosenIncludesNoAns(selection) && selection.length > 1) {
                this.currentquestion.chosen.splice(0, 1);
            }
            let length = 1;
            switch (type) {
                case "multiple":
                    length = 10;
                    break;
                case "three":
                    length = 3;
                    break;
                case "four":
                    length = 4;
                    break;
                case "five":
                    length = 5;
                    break;
                case "six":
                    length = 6;
                    break;
                case "seven":
                    length = 7;
                    break;
                case "eight":
                    length = 8;
                    break;
                case "single":
                    length = 1;
                    break;
            }
            if (selection.length > length) {
                this.currentquestion.chosen.splice(0, 1);
            }
        },

        /**
         * Redirects to the next brick in the walkthrough.
         * @param {*} currentbrick - The current brick being visited.
         */
        getNextInWalkthrough(currentbrick) {
            this.clearObjects();
            switch (currentbrick) {
                case "Wertangebot":
                    this.$router.push({
                        name: "Brick",
                        params: { hash: this.hash, name: "Kanäle" },
                    });
                    break;
                case "Schlüsselpartnerschaften":
                    this.$router.push({
                        name: "Brick",
                        params: { hash: this.hash, name: "Kostenstruktur" },
                    });
                    break;
                case "Kundensegment":
                    this.$router.push({
                        name: "Brick",
                        params: { hash: this.hash, name: "Wertangebot" },
                    });
                    break;
                case "Schlüsselressourcen":
                    this.$router.push({
                        name: "Brick",
                        params: { hash: this.hash, name: "Schlüsselaktivitäten" },
                    });
                    break;
                case "Schlüsselaktivitäten":
                    this.$router.push({
                        name: "Brick",
                        params: { hash: this.hash, name: "Schlüsselpartnerschaften" },
                    });
                    break;
                case "Einnahmequellen":
                    this.$router.push({
                        name: "Brick",
                        params: { hash: this.hash, name: "Schlüsselressourcen" },
                    });
                    break;
                case "Kostenstruktur":
                    this.$router.push({
                        name: "BusinessModel",
                        params: { hash: this.hash },
                    });
                    break;
                case "Kundenbeziehung":
                    this.$router.push({
                        name: "Brick",
                        params: { hash: this.hash, name: "Einnahmequellen" },
                    });
                    break;
                case "Kanäle":
                    this.$router.push({
                        name: "Brick",
                        params: { hash: this.hash, name: "Kundenbeziehung" },
                    });
                    break;
            }
        },

        /**
         * Calculates the progress made in the question sequence.
         * @returns - The progress made in the question sequence.
         */
        amount() {
            return (this.seqidx * 100) / this.questionsequences.length;
        },
    },
};



// WEBPACK FOOTER //
// ./src/components/company/businessmodel/tile/brick.js